import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { Navbar } from '../components/Navbar';
import { Footer } from '../components/Footer';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import { 
  Share2, 
  Download, 
  Copy, 
  ChevronLeft,
  QrCode,
  Loader2,
  Check,
  Twitter,
  Facebook,
  MessageCircle,
  Instagram
} from 'lucide-react';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function SharePoster() {
  const navigate = useNavigate();
  const { user, isAuthenticated } = useAuth();
  const [loading, setLoading] = useState(true);
  const [referralStats, setReferralStats] = useState(null);
  const [qrCodeUrl, setQrCodeUrl] = useState(null);
  const [copied, setCopied] = useState(false);
  const [pricing, setPricing] = useState({
    referrer_credit_percent: 10,
    referee_credit_percent: 10,
    subscription_price: 20
  });
  const canvasRef = useRef(null);

  // Calculate dynamic credit amounts
  const referrerCredit = (pricing.subscription_price * pricing.referrer_credit_percent / 100).toFixed(2);
  const refereeCredit = (pricing.subscription_price * pricing.referee_credit_percent / 100).toFixed(2);

  useEffect(() => {
    // Fetch pricing first (no auth required)
    fetchPricing();
    
    if (isAuthenticated && user?.referral_code) {
      fetchReferralStats();
      fetchQRCode();
    } else {
      setLoading(false);
    }
  }, [isAuthenticated, user]);

  const fetchPricing = async () => {
    try {
      const response = await fetch(`${API_URL}/api/pricing/public`);
      if (response.ok) {
        const data = await response.json();
        setPricing({
          referrer_credit_percent: data.referrer_credit_percent || 10,
          referee_credit_percent: data.referee_credit_percent || 10,
          subscription_price: data.subscription_price || 20
        });
      }
    } catch (err) {
      console.error('Failed to fetch pricing:', err);
    }
  };

  const fetchReferralStats = async () => {
    try {
      const response = await fetch(`${API_URL}/api/referral/stats`, {
        credentials: 'include'
      });
      if (response.ok) {
        const data = await response.json();
        setReferralStats(data);
      }
    } catch (err) {
      console.error('Failed to fetch referral stats:', err);
    }
  };

  const fetchQRCode = async () => {
    try {
      const response = await fetch(`${API_URL}/api/qr-code/${user.referral_code}`, {
        credentials: 'include'
      });
      if (response.ok) {
        const blob = await response.blob();
        setQrCodeUrl(URL.createObjectURL(blob));
      }
    } catch (err) {
      console.error('Failed to fetch QR code:', err);
    } finally {
      setLoading(false);
    }
  };

  const referralLink = user?.referral_code 
    ? `${window.location.origin}/signup?ref=${user.referral_code}`
    : '';

  const copyToClipboard = async () => {
    try {
      await navigator.clipboard.writeText(referralLink);
      setCopied(true);
      toast.success('Referral link copied!');
      setTimeout(() => setCopied(false), 2000);
    } catch (err) {
      toast.error('Failed to copy link');
    }
  };

  const downloadPoster = () => {
    if (!canvasRef.current || !qrCodeUrl) return;

    const canvas = canvasRef.current;
    const ctx = canvas.getContext('2d');
    
    // Set canvas size (Instagram story size)
    canvas.width = 1080;
    canvas.height = 1920;

    // Background gradient
    const gradient = ctx.createLinearGradient(0, 0, 0, canvas.height);
    gradient.addColorStop(0, '#0a0a0a');
    gradient.addColorStop(0.5, '#1a1a2e');
    gradient.addColorStop(1, '#0a0a0a');
    ctx.fillStyle = gradient;
    ctx.fillRect(0, 0, canvas.width, canvas.height);

    // Add brand text
    ctx.fillStyle = '#ef4444';
    ctx.font = 'bold 60px system-ui, -apple-system, sans-serif';
    ctx.textAlign = 'center';
    ctx.fillText('SPORTSANALYSISAB', canvas.width / 2, 200);

    ctx.fillStyle = '#a1a1aa';
    ctx.font = '30px system-ui, -apple-system, sans-serif';
    ctx.fillText('ALBERTA SPORTS ANALYTICS', canvas.width / 2, 260);

    // Main message
    ctx.fillStyle = '#ffffff';
    ctx.font = 'bold 72px system-ui, -apple-system, sans-serif';
    ctx.fillText(`GET $${refereeCredit} CREDIT`, canvas.width / 2, 500);

    ctx.fillStyle = '#22c55e';
    ctx.font = 'bold 48px system-ui, -apple-system, sans-serif';
    ctx.fillText('WHEN YOU SIGN UP', canvas.width / 2, 580);

    // Description
    ctx.fillStyle = '#a1a1aa';
    ctx.font = '36px system-ui, -apple-system, sans-serif';
    ctx.fillText('Statistical game previews and', canvas.width / 2, 700);
    ctx.fillText('probability estimates for NHL, NBA,', canvas.width / 2, 750);
    ctx.fillText('NFL, MLB, MLS, and more', canvas.width / 2, 800);

    // Load and draw QR code
    const qrImage = new Image();
    qrImage.crossOrigin = 'anonymous';
    qrImage.onload = () => {
      // White background for QR
      ctx.fillStyle = '#ffffff';
      ctx.fillRect(340, 920, 400, 400);
      ctx.drawImage(qrImage, 360, 940, 360, 360);

      // Scan text
      ctx.fillStyle = '#ffffff';
      ctx.font = 'bold 36px system-ui, -apple-system, sans-serif';
      ctx.fillText('SCAN TO JOIN', canvas.width / 2, 1420);

      // Referral code
      ctx.fillStyle = '#22c55e';
      ctx.font = 'bold 48px monospace';
      ctx.fillText(`CODE: ${user?.referral_code || ''}`, canvas.width / 2, 1520);

      // Footer disclaimer
      ctx.fillStyle = '#71717a';
      ctx.font = '24px system-ui, -apple-system, sans-serif';
      ctx.fillText('For entertainment purposes only. 18+', canvas.width / 2, 1800);

      // Download
      const link = document.createElement('a');
      link.download = `sportsanalysisab-referral-${user?.referral_code}.png`;
      link.href = canvas.toDataURL('image/png');
      link.click();
      toast.success('Poster downloaded!');
    };
    qrImage.src = qrCodeUrl;
  };

  const shareOnTwitter = () => {
    const text = `Get $${refereeCredit} credit on SportsAnalysisAB - the best sports analytics platform! Use my referral code: ${user?.referral_code}`;
    const url = `https://twitter.com/intent/tweet?text=${encodeURIComponent(text)}&url=${encodeURIComponent(referralLink)}`;
    window.open(url, '_blank');
  };

  const shareOnFacebook = () => {
    const url = `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(referralLink)}`;
    window.open(url, '_blank');
  };

  if (!isAuthenticated) {
    return (
      <div className="min-h-screen flex flex-col bg-background">
        <Navbar />
        <main className="flex-1 flex items-center justify-center">
          <div className="text-center">
            <Share2 className="w-12 h-12 text-muted-foreground mx-auto mb-4" />
            <h2 className="font-display text-xl font-bold uppercase mb-2">Sign In Required</h2>
            <p className="text-muted-foreground mb-4">Sign in to access social sharing features</p>
            <Button onClick={() => navigate('/login')} className="rounded-sm">
              Sign In
            </Button>
          </div>
        </main>
        <Footer />
      </div>
    );
  }

  if (!user?.referral_code) {
    return (
      <div className="min-h-screen flex flex-col bg-background">
        <Navbar />
        <main className="flex-1 flex items-center justify-center">
          <div className="text-center">
            <Share2 className="w-12 h-12 text-muted-foreground mx-auto mb-4" />
            <h2 className="font-display text-xl font-bold uppercase mb-2">Subscription Required</h2>
            <p className="text-muted-foreground mb-4">Referral program is available to subscribers only</p>
            <Button onClick={() => navigate('/billing')} className="rounded-sm">
              View Plans
            </Button>
          </div>
        </main>
        <Footer />
      </div>
    );
  }

  return (
    <div className="min-h-screen flex flex-col bg-background">
      <Navbar />
      
      <main className="flex-1">
        {/* Header */}
        <div className="border-b border-border">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
            <div className="flex items-center gap-2 mb-4">
              <Button 
                variant="ghost" 
                size="sm" 
                className="rounded-sm"
                onClick={() => navigate('/referral')}
              >
                <ChevronLeft className="w-4 h-4 mr-1" />
                Back to Referrals
              </Button>
            </div>
            <div className="flex items-center gap-3">
              <div className="w-12 h-12 bg-purple-500/10 rounded-sm flex items-center justify-center">
                <Share2 className="w-6 h-6 text-purple-400" />
              </div>
              <div>
                <h1 className="font-display text-3xl font-bold uppercase tracking-tight">
                  Share & Earn
                </h1>
                <p className="text-muted-foreground">
                  Share your referral link and earn ${referrerCredit} for each signup
                </p>
              </div>
            </div>
          </div>
        </div>

        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            {/* QR Code & Link */}
            <div className="space-y-6">
              {/* QR Code Card */}
              <div className="bg-card border border-border rounded-sm p-6 text-center">
                <h3 className="font-display text-lg font-bold uppercase tracking-tight mb-4">
                  Your QR Code
                </h3>
                
                {loading ? (
                  <div className="w-48 h-48 mx-auto bg-secondary rounded-sm flex items-center justify-center">
                    <Loader2 className="w-8 h-8 animate-spin text-muted-foreground" />
                  </div>
                ) : qrCodeUrl ? (
                  <div className="w-48 h-48 mx-auto bg-white rounded-sm p-2">
                    <img 
                      src={qrCodeUrl} 
                      alt="Referral QR Code" 
                      className="w-full h-full"
                    />
                  </div>
                ) : (
                  <div className="w-48 h-48 mx-auto bg-secondary rounded-sm flex items-center justify-center">
                    <QrCode className="w-16 h-16 text-muted-foreground" />
                  </div>
                )}
                
                <p className="text-sm text-muted-foreground mt-4">
                  Scan to join with your referral
                </p>
              </div>

              {/* Referral Link */}
              <div className="bg-card border border-border rounded-sm p-6">
                <h3 className="font-display text-lg font-bold uppercase tracking-tight mb-4">
                  Referral Link
                </h3>
                
                <div className="flex gap-2">
                  <Input 
                    value={referralLink}
                    readOnly
                    className="font-mono text-sm rounded-sm"
                  />
                  <Button 
                    variant="outline" 
                    size="icon"
                    className="rounded-sm flex-shrink-0"
                    onClick={copyToClipboard}
                    data-testid="copy-link-btn"
                  >
                    {copied ? (
                      <Check className="w-4 h-4 text-green-400" />
                    ) : (
                      <Copy className="w-4 h-4" />
                    )}
                  </Button>
                </div>

                <div className="flex items-center justify-center gap-2 mt-4">
                  <Badge variant="outline" className="font-mono">
                    Code: {user?.referral_code}
                  </Badge>
                </div>
              </div>
            </div>

            {/* Share Options & Stats */}
            <div className="space-y-6">
              {/* Download Poster */}
              <div className="bg-card border border-border rounded-sm p-6">
                <h3 className="font-display text-lg font-bold uppercase tracking-tight mb-4">
                  Download Poster
                </h3>
                <p className="text-sm text-muted-foreground mb-4">
                  Download a shareable poster with your QR code for social media
                </p>
                <Button 
                  className="w-full rounded-sm"
                  onClick={downloadPoster}
                  disabled={!qrCodeUrl}
                  data-testid="download-poster-btn"
                >
                  <Download className="w-4 h-4 mr-2" />
                  Download Poster (1080x1920)
                </Button>
              </div>

              {/* Social Share */}
              <div className="bg-card border border-border rounded-sm p-6">
                <h3 className="font-display text-lg font-bold uppercase tracking-tight mb-4">
                  Share on Social
                </h3>
                <div className="grid grid-cols-2 gap-3">
                  <Button 
                    variant="outline" 
                    className="rounded-sm"
                    onClick={shareOnTwitter}
                    data-testid="share-twitter-btn"
                  >
                    <Twitter className="w-4 h-4 mr-2" />
                    Twitter / X
                  </Button>
                  <Button 
                    variant="outline" 
                    className="rounded-sm"
                    onClick={shareOnFacebook}
                    data-testid="share-facebook-btn"
                  >
                    <Facebook className="w-4 h-4 mr-2" />
                    Facebook
                  </Button>
                </div>
              </div>

              {/* Stats */}
              {referralStats && (
                <div className="bg-green-500/5 border border-green-500/20 rounded-sm p-6">
                  <h3 className="font-display text-lg font-bold uppercase tracking-tight text-green-400 mb-4">
                    Your Referral Stats
                  </h3>
                  <div className="grid grid-cols-2 gap-4">
                    <div className="text-center">
                      <p className="font-mono text-3xl font-bold">{referralStats.total_referrals}</p>
                      <p className="text-sm text-muted-foreground">Total Referrals</p>
                    </div>
                    <div className="text-center">
                      <p className="font-mono text-3xl font-bold text-green-400">
                        ${referralStats.credits_earned?.toFixed(2) || '0.00'}
                      </p>
                      <p className="text-sm text-muted-foreground">Credits Earned</p>
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* Hidden canvas for poster generation */}
          <canvas ref={canvasRef} style={{ display: 'none' }} />
        </div>
      </main>

      <Footer />
    </div>
  );
}
